
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, CircularProgress, Alert, Select, MenuItem,
    FormControl, InputLabel, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, Grid, IconButton, Collapse, Card, CardContent, Chip, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { 
    getAllUsers, toggleUserVerifiedStatus, toggleUserActiveStatus,
    deleteUser, updateUser,
} from '../api/admin';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonOffIcon from '@mui/icons-material/PersonOff';

const StyledPaper = styled(Paper)(({ theme }) => ({
   padding: theme.spacing(1, 2, 1, 2), 
    marginBottom: theme.spacing(1),
}));

const ActionButton = styled(Button)(({ theme }) => ({
    minWidth: 0,
    padding: '2px 8px',
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    borderRadius: 8,
}));
const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();
  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    padding: theme.spacing(0.5),
}));

const StyledTableRow = styled(TableRow)(({ theme, index }) => ({
    backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff',
    '& > td': {
        borderBottom: 'none',
    },
}));

const DetailSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Typography
            variant="subtitle2"
            sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                color: "#11224d",
                mb: 0.5,
                borderBottom: '1px solid #ddd',
                pb: 0.5,
            }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

const UserRow = ({ user, idx, onToggleVerified, onToggleActive, onDelete, onEdit }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <StyledTableRow index={idx} hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
                <TableCell padding="checkbox" sx={{ borderBottom: open ? 'none' : null }}>
                    <IconButton size="small" sx={{ p: 0, color: 'primary.main' }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{user.company_name || 'N/A'}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{user.email}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize', fontSize: '0.75rem', py: 0.5 }}>
                    <Chip
                        label={user.role}
                        size="small"
                        sx={{
                            fontSize: '0.65rem',
                            height: '20px',
                            backgroundColor:
                                user.role === 'shipper' ? '#c8e6c9' : 
                                user.role === 'carrier' ? '#fff9c4' : 
                                user.role === 'broker'  ? '#bbdefb' : '#e0e0e0',
                            color:
                                user.role === 'shipper' ? '#1b5e20' :
                                user.role === 'carrier' ? '#f57f17' :
                                user.role === 'broker'  ? '#0d47a1' : '#000000', 
                        }}
                    />
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{dayjs(user.created_at).format('MM/DD/YYYY')}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.5, textAlign: 'center' }}>
                    {user.is_verified ? 'Yes' : 'No'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.5, textAlign: 'center' }}>
                    {user.deleted_by_user === true ? (
                        <Chip icon={<PersonOffIcon />} label="Deleted" color="error" size="small" variant="outlined" />
                    ) : user.deleted_at ? (
                        <Chip icon={<PersonOffIcon />} label="Deleted" color="error" size="small" />
                    ) : user.is_active ? (
                        <Chip label="Active" color="success" size="small" variant="outlined" />
                    ) : (
                        <Chip label="Inactive" size="small" />
                    )}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ py: 0.25 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {user.deleted_by_user === true ? (
                            <Tooltip title="Permanently remove user record">
                                <StyledIconButton size="small" color="error" onClick={() => onDelete(user)}>
                                    <DeleteIcon fontSize="small" />
                                </StyledIconButton>
                            </Tooltip>
                        ) : user.deleted_at ? (
                            <Typography variant="caption" color="error" sx={{ fontStyle: 'italic', mr: 1 }}>Deleted on {dayjs(user.deleted_at).format('MM/DD/YY')}</Typography>
                        ) : (
                        <>
                        <ActionButton variant="outlined" size="small" onClick={() => onToggleVerified(user)}>
                            {user.is_verified ? 'Unverify' : 'Verify'}
                        </ActionButton>
                        <ActionButton variant="outlined" size="small" onClick={() => onToggleActive(user)}>
                            {user.is_active ? 'Deactivate' : 'Activate'}
                        </ActionButton>
                        <StyledIconButton size="small" onClick={() => onEdit(user)}>
                            <EditIcon fontSize="small" />
                        </StyledIconButton>
                        <StyledIconButton size="small" onClick={() => onDelete(user)}>
                            <DeleteIcon fontSize="small" />
                        </StyledIconButton>
                        </>
                        )}
                    </Box>
                </TableCell>
            </StyledTableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Card sx={{ m: 1, border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <DetailSection title="Contact Information">
                                            <Typography variant="caption" display="block"><b>DBA Name:</b> {user.dba_name || 'N/A'}</Typography>
                                            <Typography variant="caption" display="block"><b>Phone:</b> {user.phone_number || 'N/A'}</Typography>
                                            <Typography variant="caption" display="block"><b>Email:</b> {user.email || 'N/A'}</Typography>
                                            {(user.role === 'carrier' || user.role === 'broker') && <Typography variant="caption" display="block"><b>FMCSA-Email:</b> {user.fmcsa_email || 'N/A'}</Typography>}
                                        </DetailSection>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DetailSection title="Address">
                                            <Typography variant="caption" display="block"><b>Street:</b> {user.street || 'N/A'}</Typography>
                                            <Typography variant="caption" display="block"><b>City, State, Zip:</b> {user.city_st_zip || 'N/A'}</Typography>
                                        </DetailSection>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
    <DetailSection title="Role Specifics">
        {user.role === 'shipper' ? (
            <Typography variant="caption" display="block"><b>Employees:</b> {user.employees || 'N/A'}</Typography>
        ) : (
            <>
                <Typography variant="caption" display="block"><b>DOT Number:</b> {user.dot_number || 'N/A'}</Typography>
                {user.mc_number && user.mc_number !== 'MC-' && (
                    <Typography variant="caption" display="block"><b>MC Number:</b> {user.mc_number}</Typography>
                )}
                <Typography variant="caption" display="block"><b>Power Units:</b> {user.power_units || 'N/A'}</Typography>
            </>
        )}
    </DetailSection>
</Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const UserManagement = () => { 
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // Keep error state for API calls
    const [filters, setFilters] = useState({ role: 'all', verified: 'all', active: 'all' });
    const [editUser, setEditUser] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: () => {} });

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await getAllUsers();
            setUsers(data.users || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch users.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleToggleVerified = async (user) => {
        setConfirmDialog({
            open: true,
            title: `Confirm Verification Change`,
            message: `Are you sure you want to ${user.is_verified ? 'unverify' : 'verify'} ${user.email}?`,
            onConfirm: async () => {
                try {
                    const updatedUser = await toggleUserVerifiedStatus(user.id, user.role);
                    setUsers(users.map(u => u.id === user.id && u.role === user.role ? { ...u, is_verified: updatedUser.is_verified } : u));
                } catch (err) {
                    setError('Failed to update verification status.');
                } finally {
                    setConfirmDialog({ open: false, onConfirm: () => {} });
                }
            }
        });
    };

    const handleToggleActive = async (user) => {
        setConfirmDialog({
            open: true,
            title: `Confirm Status Change`,
            message: `Are you sure you want to ${user.is_active ? 'deactivate' : 'activate'} ${user.email}?`,
            onConfirm: async () => {
                try {
                    const updatedUser = await toggleUserActiveStatus(user.id, user.role);
                    setUsers(users.map(u => u.id === user.id && u.role === user.role ? { ...u, is_active: updatedUser.is_active } : u));
                } catch (err) {
                    setError('Failed to update active status.');
                } finally {
                    setConfirmDialog({ open: false, onConfirm: () => {} });
                }
            }
        });
    };

    const handleDeleteUser = (user) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete ${user.email}? This action is permanent.`,
            onConfirm: async () => {
                try {
                    await deleteUser(user.id, user.role);
                    setUsers(users.filter(u => !(u.id === user.id && u.role === user.role)));
                    setConfirmDialog({ open: false, onConfirm: () => {} });
                } catch (err) {
                    setError('Failed to delete user.');
                    setConfirmDialog({ open: false, onConfirm: () => {} });
                }
            }
        });
    };

    const handleEditUser = (user) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Edit',
            message: `Do you want to edit the details for ${user.email}?`,
            onConfirm: () => {
                setEditUser(user);
                setConfirmDialog({ open: false, onConfirm: () => {} });
            }
        });
    };

    const handleUpdateUser = async () => {
        try {
            const { id, role, ...data } = editUser;
            const updatedUser = await updateUser(id, role, data);
            setUsers(users.map(u => u.id === id && u.role === role ? { ...u, ...updatedUser } : u));
            setEditUser(null);
        } catch (err) {
            setError('Failed to update user details.');
        }
    };

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            if (!user) return false;
            const roleMatch = filters.role === 'all' || user.role === filters.role;
            const verifiedMatch = filters.verified === 'all' || String(user.is_verified) === filters.verified;
            
            let activeMatch = true;
            if (filters.active === 'true') {
                activeMatch = user.is_active === true && user.deleted_by_user !== true;
            } else if (filters.active === 'false') {
                activeMatch = user.is_active === false || user.deleted_by_user === true;
            }

            return roleMatch && verifiedMatch && activeMatch;
        });
    }, [users, filters]);

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
           

            <StyledPaper>
                
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
                <FormControl size="small" sx={{ minWidth: 150, backgroundColor: 'white' }}>
                    <InputLabel>Role</InputLabel>
                    <Select name="role" value={filters.role} label="Role" onChange={handleFilterChange}>
                        <MenuItem value="all">All Roles</MenuItem>
                        <MenuItem value="shipper">Shipper</MenuItem>
                        <MenuItem value="broker">Broker</MenuItem> 
                        <MenuItem value="carrier">Carrier</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150, backgroundColor: 'white' }}>
                    <InputLabel>Verified Status</InputLabel>
                    <Select name="verified" value={filters.verified} label="Verified Status" onChange={handleFilterChange}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="true">Verified</MenuItem>
                        <MenuItem value="false">Unverified</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150, backgroundColor: 'white' }}>
                    <InputLabel>Active Status</InputLabel>
                    <Select name="active" value={filters.active} label="Active Status" onChange={handleFilterChange}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            </StyledPaper>

            <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 130px)' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ backgroundColor: '#11224d' }} />
                            {["Company Name", "Email", "Role", "Registered", "E-Verified", "Active", "Actions"].map((head, index) => (
                                <TableCell key={head} sx={{ color: 'white', fontWeight: 'bold', py: 1, fontSize: '0.75rem', textAlign: index > 2 ? 'center' : 'left', backgroundColor: '#11224d' }}>
                                    {head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.map(user => (
                            <UserRow
                                key={`${user.role}-${user.id}`}
                                user={user}
                                idx={users.indexOf(user)}
                                onToggleVerified={handleToggleVerified}
                                onToggleActive={handleToggleActive}
                                onDelete={handleDeleteUser}
                                onEdit={handleEditUser}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit User: {editUser?.email}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Company Name"
                                value={editUser?.company_name || ''}
                                onChange={(e) => setEditUser({ ...editUser, company_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="DBA Name"
                                value={editUser?.dba_name || ''}
                                onChange={(e) => setEditUser({ ...editUser, dba_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Street Address"
                                value={editUser?.street || ''}
                                onChange={(e) => setEditUser({ ...editUser, street: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="City, State, Zip"
                                value={editUser?.city_st_zip || ''}
                                onChange={(e) => setEditUser({ ...editUser, city_st_zip: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Phone Number"
                                value={editUser?.phone_number || ''}
                                onChange={(e) => setEditUser({ ...editUser, phone_number: e.target.value })}
                            />
                        </Grid>
                        {(editUser?.role === 'carrier' || editUser?.role === 'broker') && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="FMCSA-Email"
                                    value={editUser?.fmcsa_email || ''}
                                    onChange={(e) => setEditUser({ ...editUser, fmcsa_email: e.target.value })}
                                />
                            </Grid>
                        )}
                        {editUser?.role === 'carrier' && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="DOT Number"
                                        value={editUser?.dot_number || ''}
                                        onChange={(e) => setEditUser({ ...editUser, dot_number: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Power Units"
                                        type="number"
                                        value={editUser?.power_units || ''}
                                        onChange={(e) => setEditUser({ ...editUser, power_units: e.target.value })}
                                    />
                                </Grid>
                            </>
                        )}
                        {editUser?.role === 'shipper' && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Employees"
                                    type="number"
                                    value={editUser?.employees || ''}
                                    onChange={(e) => setEditUser({ ...editUser, employees: e.target.value })}
                                />
                            </Grid>
                        )}
                        {editUser?.role === 'broker' && ( 
    <>
    <Grid item xs={12} sm={4}>
        <TextField
            fullWidth
            size="small"
            label="DOT Number"
            value={editUser?.dot_number || ''}
            onChange={(e) => setEditUser({ ...editUser, dot_number: e.target.value })}
        />
    </Grid>
    <Grid item xs={12} sm={4}>
        <TextField
            fullWidth
            size="small"
            label="MC Number"
            value={editUser?.mc_number || ''}
            onChange={(e) => setEditUser({ ...editUser, mc_number: e.target.value })}
        />
    </Grid>
    <Grid item xs={12} sm={4}>
        <TextField
            fullWidth
            size="small"
            label="Power Units"
            type="number"
            value={editUser?.power_units || ''}
            onChange={(e) => setEditUser({ ...editUser, power_units: e.target.value })}
        />
    </Grid>
    </>
)}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditUser(null)}>Cancel</Button>
                    <Button onClick={handleUpdateUser} variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
                <DialogTitle>{confirmDialog.title || 'Confirm Action'}</DialogTitle>
                <DialogContent>
                    <Typography>{confirmDialog.message || 'Are you sure?'}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button onClick={confirmDialog.onConfirm} color="error" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default UserManagement;